import { useState } from "react";
import { SIGNUP_BONUS } from "@/lib/constants";
import { Shirt, MapPin, Clock, Ticket, ChevronRight, CheckCircle2, CalendarDays } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useTrikotRedemption } from "@/lib/trikot";
import { useAuth } from "@/hooks/useAuth";
import { ApiError } from "@/api/client";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import wmTrikot from "@/assets/wm-trikot-icon.png";
import wmTrikotAmbiente from "@/assets/wm-trikot-kabine-landscape.jpg";
import wmTrikotFreigestellt from "@/assets/wm-trikot-freigestellt.png";

type Variant = "hero" | "card" | "compact";

function RedemptionPanel() {
  const { user } = useAuth();
  const { redemption, loading, activate, markCollected } = useTrikotRedemption();
  const [busy, setBusy] = useState(false);
  const [collectBusy, setCollectBusy] = useState(false);

  if (!user) {
    return (
      <div className="rounded-lg border-2 border-accent/40 bg-accent/5 p-3 text-sm">
        <div className="font-display font-bold mb-1">🎽 Trikot einlösen</div>
        <p className="text-muted-foreground text-[13px]">Melde dich an oder registriere dich, um dein Gratis-Trikot vor Ort abzuholen.</p>
      </div>
    );
  }

  if (loading) {
    return <div className="rounded-lg border bg-muted/30 h-24 animate-pulse" />;
  }

  if (redemption?.status === "collected") {
    return (
      <div className="rounded-lg border-2 border-success bg-success/10 p-4 text-center">
        <CheckCircle2 className="h-8 w-8 text-success mx-auto mb-1" />
        <div className="font-display font-extrabold text-lg">🎽 Trikot erfolgreich abgeholt</div>
        <div className="text-xs text-muted-foreground mt-1">
          {redemption.collected_at && new Date(redemption.collected_at).toLocaleString("de-AT", { dateStyle: "medium", timeStyle: "short" })}
        </div>
      </div>
    );
  }

  if (redemption?.status === "active") {
    // Self-collect via the auth-only /api/trikot/me/collect endpoint.
    // Replaces the previous slider, which (a) sometimes failed for
    // unknown reasons on certain phones and (b) was silently 403'ing
    // because the old code called the staff-only /collect endpoint with
    // the user's own pickup_code, which only worked for admin/staff.
    // Error paths now surface a real toast instead of springing back
    // silently.
    const collect = async () => {
      setCollectBusy(true);
      try {
        await markCollected();
        confetti({ particleCount: 120, spread: 80, origin: { y: 0.6 } });
        toast.success("Trikot abgeholt 🎽");
      } catch (err) {
        if (err instanceof ApiError) {
          if (err.status === 409) {
            toast.info("Trikot wurde bereits als abgeholt markiert.");
          } else if (err.status === 401 || err.status === 403) {
            toast.error("Bitte erneut anmelden und nochmal versuchen.");
          } else {
            toast.error(err.message || "Bestätigung fehlgeschlagen");
          }
        } else {
          // Network / unexpected — give the user a concrete next step.
          toast.error("Keine Verbindung. Bitte Verbindung prüfen und nochmal versuchen.");
        }
      } finally {
        setCollectBusy(false);
      }
    };
    return (
      <div className="rounded-lg border-2 border-accent bg-accent/10 p-4 space-y-3">
        <div className="text-center">
          <div className="font-display font-extrabold text-lg">🎽 Bereit zur Abholung</div>
          <p className="text-[12px] text-muted-foreground mt-1">
            Bitte erst <strong>vor Ort bei Englputzeder</strong> gemeinsam mit
            der/dem Mitarbeiter:in bestätigen.
          </p>
        </div>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              disabled={collectBusy}
              className="w-full h-12 bg-success text-white hover:bg-success/90 font-bold gap-2"
            >
              {collectBusy ? "Bestätige …" : <><CheckCircle2 className="h-5 w-5" /> Trikot abgeholt bestätigen</>}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Trikot wirklich als abgeholt markieren?</AlertDialogTitle>
              <AlertDialogDescription>
                Bitte nur drücken, wenn du dein Englputzeder WM-Trikot wirklich
                erhalten hast. Nach Bestätigung kann es <b>nicht erneut</b>{" "}
                eingelöst werden.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={collectBusy}>Abbrechen</AlertDialogCancel>
              <AlertDialogAction
                onClick={collect}
                disabled={collectBusy}
                className="bg-success text-white hover:bg-success/90"
              >
                {collectBusy ? "Bestätige …" : "Ja, bestätigen"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        <p className="text-[10px] text-muted-foreground text-center italic">
          Nach Bestätigung kann das Trikot nicht erneut eingelöst werden.
        </p>
      </div>
    );
  }

  // pending → no row yet
  return (
    <div className="rounded-lg border-2 border-accent bg-accent/10 p-4 text-center space-y-3">
      <div className="font-display font-extrabold text-lg">🎽 Trikot einlösen</div>
      <p className="text-[13px] text-muted-foreground">
        Tippe auf „Jetzt einlösen". Vor Ort bei Englputzeder ziehst du dann
        gemeinsam mit der/dem Mitarbeiter:in den Slider zur Abholung.
      </p>
      <Button
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          try { await activate(); toast.success("Code aktiviert ✅"); }
          catch (e: any) { toast.error(e?.message ?? "Fehler"); }
          finally { setBusy(false); }
        }}
        className="w-full h-12 bg-accent text-accent-foreground hover:bg-accent/90 font-bold"
      >
        {busy ? "Aktiviere …" : "Jetzt einlösen 🎟️"}
      </Button>
    </div>
  );
}

function BonusDetails() {
  return (
    <div className="space-y-4 text-sm">
      <div className="rounded-lg overflow-hidden border">
        <img
          src={wmTrikotAmbiente}
          alt="Englputzeder WM-Trikot in der Kabine — Österreich-Trikot Nr. 26"
          className="w-full aspect-[16/9] object-cover"
          loading="lazy"
        />
      </div>

      <RedemptionPanel />

      <div className="rounded-lg border-2 border-accent/40 p-3 bg-accent/5">
        <div className="flex items-center gap-2 mb-1.5">
          <Shirt className="h-4 w-4 text-accent" />
          <div className="font-display font-bold text-base">Gratis Englputzeder WM-Trikot</div>
        </div>
        <p className="text-muted-foreground text-[13px] leading-relaxed">
          Hol dir dein offizielles Englputzeder WM-Trikot persönlich bei uns ab — wir
          freuen uns, dich kennenzulernen!
        </p>
        <div className="mt-3 space-y-1.5 text-[13px]">
          <div className="flex items-start gap-2">
            <CalendarDays className="h-4 w-4 text-accent shrink-0 mt-0.5" />
            <div className="text-muted-foreground">
              <span className="font-semibold text-foreground">Abholung ab Mo, 1. Juni 2026</span>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <MapPin className="h-4 w-4 text-accent shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-foreground">Englputzeder GmbH</div>
              <div className="text-muted-foreground">Furt 44, 4754 Andrichsfurt</div>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <Clock className="h-4 w-4 text-accent shrink-0 mt-0.5" />
            <div className="text-muted-foreground">
              <span className="font-semibold text-foreground">Mo–Fr 8:00–17:00</span> Uhr
            </div>
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground italic mt-3">
          Trikots sind streng limitiert — solange der Vorrat reicht.
        </p>
      </div>

      <div className="rounded-lg border-2 border-accent/40 p-3 bg-accent/5">
        <div className="flex items-center gap-2 mb-1.5">
          <Ticket className="h-4 w-4 text-accent" />
          <div className="font-display font-bold text-base">{SIGNUP_BONUS.voucherEur} € Gutschein</div>
        </div>
        <p className="text-muted-foreground text-[13px] leading-relaxed">
          Deinen {SIGNUP_BONUS.voucherEur} € Gutschein erhältst du gemeinsam mit dem Trikot
          direkt bei der Abholung an unserem Standort.
        </p>
        <p className="text-[11px] text-muted-foreground mt-3 leading-relaxed">
          Mindestbestellwert {SIGNUP_BONUS.minOrderEur.toLocaleString("de-DE")} €. Keine Barablöse.
          Gültig bis 31. August 2026.
        </p>
      </div>

      <p className="text-[11px] text-muted-foreground text-center">
        Fragen? <a href="mailto:anfrage@englputzeder.at" className="underline">anfrage@englputzeder.at</a> · Tel.: +43 7750-20194
      </p>
    </div>
  );
}

export function SignupBonusBanner({ variant = "card" }: { variant?: Variant }) {
  const [open, setOpen] = useState(false);
  const { redemption } = useTrikotRedemption();
  const status = redemption?.status; // undefined | "active" | "collected"
  const statusBadge =
    status === "collected" ? { text: "✅ Trikot abgeholt", cls: "bg-success text-white" }
    : status === "active" ? { text: "🎽 Bereit zur Abholung", cls: "bg-card text-foreground border border-accent" }
    : { text: "Jetzt einlösen →", cls: "bg-card text-foreground" };

  const trigger = (() => {
    if (variant === "hero") {
      return (
        <button
          type="button"
          className="w-full text-left rounded-md bg-gradient-to-br from-accent to-accent/70 text-accent-foreground p-3 shadow-pop flex items-center gap-3 tap hover:brightness-105 transition"
        >
          <img
            src={wmTrikot}
            alt="WM-Trikot"
            className="h-12 w-12 object-contain shrink-0 drop-shadow"
          />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-wider opacity-80 leading-none">
              Anmelde-Geschenk
            </div>
            <div className="font-display font-extrabold text-base leading-tight mt-0.5">
              Gratis WM-Trikot + {SIGNUP_BONUS.voucherEur} € Gutschein
            </div>
            {status && (
              <div className="text-[10px] font-semibold opacity-90 mt-1 leading-tight">
                <span className={`inline-block rounded-pill px-2 py-0.5 text-[10px] font-bold ${statusBadge.cls}`}>{statusBadge.text}</span>
              </div>
            )}
          </div>
        </button>
      );
    }

    if (variant === "compact") {
      return (
        <button
          type="button"
          className="flex items-center gap-2 rounded-pill bg-accent/15 border border-accent/30 px-3 py-1.5 text-[11px] font-bold text-foreground tap hover:bg-accent/25 transition"
        >
          <Shirt className="h-3.5 w-3.5 text-accent" />
          <span>Anmelden = Gratis Trikot + 50 € Gutschein</span>
        </button>
      );
    }

    return (
      <button
        type="button"
        className="w-full text-left bg-card rounded-lg shadow-card p-4 flex items-center gap-3 border-2 border-accent/40 tap hover:bg-accent/5 transition"
      >
        <img src={wmTrikot} alt="WM-Trikot" className="h-14 w-14 object-contain shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-wider text-accent leading-none">
            Dein Willkommens-Geschenk
          </div>
          <div className="font-display font-extrabold text-base leading-tight mt-0.5">
            Gratis Englputzeder WM-Trikot
            <span className="text-muted-foreground font-bold"> + </span>
            {SIGNUP_BONUS.voucherEur} € Gutschein
          </div>
          <div className="text-[11px] text-muted-foreground mt-1 leading-tight">
            <span className={`inline-block rounded-pill px-2 py-0.5 text-[10px] font-bold ${statusBadge.cls}`}>{statusBadge.text}</span>
          </div>
        </div>
        <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
      </button>
    );
  })();

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">🎽 Dein Willkommens-Geschenk</DialogTitle>
          <DialogDescription>
            So kommst du an dein Trikot und deinen Gutschein.
          </DialogDescription>
        </DialogHeader>
        <BonusDetails />
      </DialogContent>
    </Dialog>
  );
}
